import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { BdPageable, Paginator } from '@bindoc-projects/platform-lib/data';
import { BdPermissionsResourcesAdminStore } from '../../store/resources/resources-admin.store';
import {
  AccessGraph,
  ShareableObj,
  ShareableType,
  ShareTargetType,
} from '@bindoc-projects/resource-domain-lib';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiEndpointKey } from '@bindoc-projects/app-config-domain-lib';

@Injectable({
  providedIn: 'root',
})
export class BdPermissionsResourcesAdminService {
  private _httpClient = inject(HttpClient);
  private _environmentService = inject(BdAppEnvironmentService);

  private _store: BdPermissionsResourcesAdminStore;

  constructor() {
    this._store = new BdPermissionsResourcesAdminStore(
      this._environmentService.load(ApiEndpointKey.BD_PERMISSION_RESOURCE_URL),
    );
  }

  public searchForTarget(
    page: Paginator,
    shareTargetType: ShareTargetType,
    shareTargetId: string,
    shareableType?: ShareableType,
    labels?: string[],
  ): Observable<BdPageable<ShareableObj>> {
    const url = this._store.byTarget(shareTargetType, shareTargetId);
    let queryParams = page?.appendTo(new HttpParams()) ?? new HttpParams();

    if (shareableType) {
      queryParams = queryParams.set('shareableType', shareableType);
    }

    if (labels && labels.length > 0) {
      queryParams = queryParams.appendAll({ label: labels });
    }

    return this._httpClient.get<BdPageable<ShareableObj>>(url, {
      params: queryParams,
    });
  }

  /**
   * The access graph of a shareable (BDA-5988), which replaced
   * `allowed-permissions-per-share-target`. Unlike the dialog's use of it, the
   * admin view asks for no company context — an administrator inspects the
   * resource itself, not one Kunde's view of it.
   */
  public permissions(
    shareTargetType: ShareableType,
    shareTargetId: string,
  ): Observable<AccessGraph> {
    const url = this._store.accessGraph(shareTargetType, shareTargetId);
    return this._httpClient.get<AccessGraph>(url);
  }

  public search(
    page?: Paginator,
    shareableType?: ShareableType,
    labels?: string[],
  ): Observable<BdPageable<ShareableObj>> {
    const url = this._store.all();
    let queryParams = page?.appendTo(new HttpParams()) ?? new HttpParams();

    if (shareableType) {
      queryParams = queryParams.set('shareableType', shareableType);
    }

    if (labels && labels.length > 0) {
      queryParams = queryParams.appendAll({ label: labels });
    }

    return this._httpClient.get<BdPageable<ShareableObj>>(url, {
      params: queryParams,
    });
  }

  public availableShareableTypes(): Observable<ShareableType[]> {
    const url = this._store.available();
    return this._httpClient
      .get<{ [shareableType: string]: ShareableType }>(url)
      .pipe(map((it) => Object.values(it)));
  }
}
